import { useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '@/lib/logger';

/**
 * 从缓存API获取探索内容的React钩子
 * 支持分页和筛选
 */
export function useCachedExploreContent({
  contentType = 'videos', // 'videos' 或 'images'
  infiniteScroll = false,
  featuredOnly = false, 
  email = null,
  type = null,
  initialPage = 1,
  itemsPerPage = 6, // 🔥 修改：默认改为6
  // 🔥 新增：随机性参数
  enableRandom = true,
  randomSeed = null
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(initialPage);
  const [totalCount, setTotalCount] = useState(0);
  
  // 防止重复加载和重试计数
  const loadingRef = useRef(false);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 2;
  
  // 构建API URL
  const getApiUrl = useCallback((pageNum) => {
    const apiBase = `/api/explore/${contentType}`;
    
    const params = new URLSearchParams();
    params.append('page', pageNum.toString());
    params.append('limit', itemsPerPage.toString());
    
    if (featuredOnly) {
      params.append('featuredOnly', 'true');
    }
    
    if (email) {
      params.append('email', email);
    }
    
    if (type) {
      params.append('type', type);
    }
    
    // 🔥 新增：添加随机性参数
    if (enableRandom) {
      params.append('random', 'true');
      if (randomSeed !== null) {
        params.append('seed', randomSeed.toString());
      } else {
        // 如果没有指定种子，使用页面号作为种子的一部分
        params.append('seed', (pageNum * 1000 + Math.floor(Math.random() * 1000)).toString());
      }
    }
    
    return `${apiBase}?${params.toString()}`;
  }, [contentType, featuredOnly, email, type, itemsPerPage, enableRandom, randomSeed]);

  // 去重函数 - 基于task_id去重
  const deduplicateItems = useCallback((newItems, existingItems = []) => {
    if (!Array.isArray(newItems) || newItems.length === 0) {
      return newItems;
    }

    // 创建现有项目的ID集合
    const existingIds = new Set(existingItems.map(item => item.task_id || item.id));

    // 过滤掉重复的项目
    const uniqueItems = newItems.filter(item => {
      const itemId = item.task_id || item.id;
      if (!itemId) {
        logger.warn('Item without ID found:', item);
        return true; // 保留没有ID的项目，但记录警告
      }
      return !existingIds.has(itemId);
    });

    if (uniqueItems.length !== newItems.length) {
      logger.debug(`Deduplication: ${newItems.length} -> ${uniqueItems.length} items`);
    }

    return uniqueItems;
  }, []);

  // 获取内容数据
  const fetchContent = useCallback(async (pageNum, append = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      setLoading(true);
      setError(null);

      const url = getApiUrl(pageNum);
      logger.debug(`Fetching content from: ${url}`);

      const response = await fetch(url);

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`API error (${response.status}): ${errorText}`);
        throw new Error(`Failed to fetch ${contentType}: ${response.statusText}`);
      }

      const data = await response.json();

      logger.debug(`Received ${contentType} data:`, {
        count: data.count || 'N/A',
        hasMore: data.hasMore || false,
        itemsCount: data.videos?.length || data[contentType]?.length || 0,
        page: pageNum,
        itemsPerPage,
        calculatedHasMore: (data.count || 0) > pageNum * itemsPerPage
      });

      if (!data.videos && !data[contentType]) {
        throw new Error(`Invalid response format for ${contentType}`);
      }

      const contentItems = data.videos || data[contentType] || [];

      // 🔥 新增：去重处理
      if (append) {
        // 追加模式：与现有数据去重
        const uniqueItems = deduplicateItems(contentItems, items);
        setItems(prev => [...prev, ...uniqueItems]);
      } else {
        // 替换模式：直接设置新数据
        setItems(contentItems);
      }

      setHasMore(data.hasMore || false);
      setTotalCount(data.count || contentItems.length);

      // 重置重试计数
      retryCountRef.current = 0;

      return contentItems;
    } catch (err) {
      logger.error(`Error fetching ${contentType}:`, err);
      setError(err.message);

      // 如果是网络问题，尝试重试几次
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        logger.info(`Retrying fetch (${retryCountRef.current}/${MAX_RETRIES})...`);

        // 短暂延迟后重试
        setTimeout(() => {
          loadingRef.current = false;
          fetchContent(pageNum, append);
        }, 1000 * retryCountRef.current); // 逐次增加延迟
      }

      return [];
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [getApiUrl, contentType, deduplicateItems, items]);

  // 初始加载
  useEffect(() => {
    logger.debug(`Initial content load for ${contentType}`, {
      featuredOnly,
      email,
      type,
      initialPage,
      itemsPerPage
    });
    fetchContent(initialPage, false);
  }, [contentType, featuredOnly, email, type, initialPage, itemsPerPage]);

  // 加载更多内容
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) {
      logger.debug(`Skip loadMore - hasMore: ${hasMore}, loading: ${loading}`);
      return;
    }

    const nextPage = page + 1;
    logger.debug(`Loading more content - page ${nextPage}`);
    const newItems = await fetchContent(nextPage, true);

    if (newItems.length > 0) {
      setPage(nextPage);
    } else {
      logger.debug('No more items returned, setting hasMore to false');
      setHasMore(false);
    }
  }, [hasMore, loading, page, fetchContent]);

  // 刷新内容
  const refreshContent = useCallback(async () => {
    logger.debug('Refreshing content');
    await fetchContent(initialPage, false);
    setPage(initialPage);
  }, [initialPage, fetchContent]);

  return {
    items,
    loading,
    error,
    hasMore,
    loadMore,
    refreshContent,
    totalCount,
    page
  };
} 